import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { formatDateTime } from "../lib/utils";
import { Plus, Trash2, ShoppingCart, Printer, Search } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";

const NewPOS = () => {
  const [categories, setCategories] = useState([]);
  const [derivedProducts, setDerivedProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cart, setCart] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [quantityKg, setQuantityKg] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("walk-in");
  const [discount, setDiscount] = useState(0);
  const [taxRate, setTaxRate] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [loading, setLoading] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [editingCartIndex, setEditingCartIndex] = useState(null);
  const [editPrice, setEditPrice] = useState("");
  const [editQuantity, setEditQuantity] = useState("");
  const [saleDate, setSaleDate] = useState(new Date().toISOString().split("T")[0]);

  const customerDropdownRef = useRef(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

  useEffect(() => {
    fetchCategories();
    fetchCustomers();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target)) {
        setShowCustomerDropdown(false);
        setCustomerSearchTerm("");
      }
    };

    if (showCustomerDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCustomerDropdown]);

  useEffect(() => {
    if (selectedCategory) {
      fetchDerivedProducts(selectedCategory);
    } else {
      setDerivedProducts([]);
      setSelectedProduct("");
    }
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/main-categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategories(response.data);
    } catch (error) {
      toast.error("Failed to fetch categories");
    }
  };

  const fetchDerivedProducts = async (categoryId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_URL}/api/derived-products?main_category_id=${categoryId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDerivedProducts(response.data);
    } catch (error) {
      toast.error("Failed to fetch products");
    }
  };

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/customers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCustomers(response.data);
    } catch (error) {
      toast.error("Failed to fetch customers");
    }
  };

  // Filter customers based on search term
  const filteredCustomers = customers.filter((customer) =>
    customer.name.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
    (customer.phone && customer.phone.includes(customerSearchTerm))
  );

  // Get the display name for selected customer
  const getSelectedCustomerName = () => {
    if (selectedCustomer === "walk-in") return "Walk-in Customer";
    const customer = customers.find((c) => c.id === selectedCustomer);
    return customer ? customer.name : "Select Customer";
  };

  // Handle customer selection
  const handleCustomerSelect = (customerId) => {
    setSelectedCustomer(customerId);
    setShowCustomerDropdown(false);
    setCustomerSearchTerm("");
  };

  const addToCart = () => {
    if (!selectedCategory || !selectedProduct || !quantityKg || parseFloat(quantityKg) <= 0) {
      toast.error("Please select category, product and enter valid quantity");
      return;
    }

    const product = derivedProducts.find((p) => p.id === selectedProduct);
    const category = categories.find((c) => c.id === selectedCategory);
    if (!product || !category) return;

    const qty = parseFloat(quantityKg);
    let quantityInKg = qty;
    
    // If product is sold by package, convert packages to kg
    if (product.sale_unit === "package") {
      quantityInKg = qty * product.package_weight_kg;
    }
    
    const total = qty * product.selling_price;

    const cartItem = {
      derived_product_id: product.id,
      derived_product_name: product.name,
      main_category_id: category.id,
      main_category_name: category.name,
      quantity_kg: quantityInKg,  // Always store in kg for inventory deduction
      quantity_display: product.sale_unit === "package" ? `${qty} pkg` : `${qty} kg`,
      selling_price: product.selling_price,
      sale_unit: product.sale_unit,
      total: total,
    };

    setCart([...cart, cartItem]);
    setSelectedCategory("");
    setSelectedProduct("");
    setQuantityKg("");
    toast.success("Item added to cart");
  };

  const removeFromCart = (index) => {
    const newCart = cart.filter((_, i) => i !== index);
    setCart(newCart);
    toast.success("Item removed from cart");
  };

  const handleEditItem = (index) => {
    setEditingCartIndex(index);
    setEditPrice(cart[index].selling_price.toString());

    // Set quantity based on sale unit
    const item = cart[index];
    if (item.sale_unit === "package") {
      // Extract package count from display string (e.g., "3 pkg" -> 3)
      setEditQuantity(item.quantity_display.split(" ")[0]);
    } else {
      // For weight-based, use quantity_kg
      setEditQuantity(item.quantity_kg.toString());
    }
  };

  const handleSaveItemEdit = () => {
    if (!editPrice || parseFloat(editPrice) <= 0) {
      toast.error("Please enter a valid price");
      return;
    }

    if (!editQuantity || parseFloat(editQuantity) <= 0) {
      toast.error("Please enter a valid quantity");
      return;
    }

    const newCart = [...cart];
    const item = newCart[editingCartIndex];
    const newPrice = parseFloat(editPrice);
    const newQty = parseFloat(editQuantity);

    // Update price
    item.selling_price = newPrice;

    // Update quantity based on sale unit
    if (item.sale_unit === "package") {
      // Update both display quantity and actual kg quantity
      item.quantity_display = `${newQty} pkg`;
      // Recalculate kg based on package weight
      const product = derivedProducts.find(p => p.id === item.derived_product_id);
      if (product && product.package_weight_kg) {
        item.quantity_kg = newQty * product.package_weight_kg;
      }
    } else {
      // For weight-based, update both
      item.quantity_kg = newQty;
      item.quantity_display = `${newQty} kg`;
    }

    // Recalculate total
    item.total = newQty * newPrice;

    setCart(newCart);
    setEditingCartIndex(null);
    setEditPrice("");
    setEditQuantity("");
    toast.success("Item updated successfully");
  };

  const calculateTotals = () => {
    const subtotal = cart.reduce((sum, item) => sum + item.total, 0);
    const discountAmount = (subtotal * discount) / 100;
    const taxableAmount = subtotal - discountAmount;
    const taxAmount = (taxableAmount * taxRate) / 100;
    const total = taxableAmount + taxAmount;

    return {
      subtotal: subtotal.toFixed(2),
      discountAmount: discountAmount.toFixed(2),
      taxAmount: taxAmount.toFixed(2),
      total: total.toFixed(2),
    };
  };

  const printReceipt = (saleData) => {
    const totals = calculateTotals();
    const customer = selectedCustomer === "walk-in"
      ? null
      : customers.find((c) => c.id === selectedCustomer);
    const customerName = customer ? customer.name : "Walk-in Customer";
    const customerPhone = customer?.phone || null;

    const receiptContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Receipt</title>
        <meta charset="UTF-8">
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          @page {
            size: 3in auto;
            margin: 0;
          }
          
          body { 
            font-family: 'Courier New', monospace;
            font-size: 12px;
            width: 3in;
            max-width: 3in;
            margin: 0 auto;
            padding: 6px 8px;
            background: white;
            color: #000;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .header { 
            text-align: center; 
            margin-bottom: 6px;
            border-bottom: 2px solid #000;
            padding-bottom: 6px;
          }
          
          .logo {
            width: 80px;
            height: 80px;
            margin: 0 auto 4px;
            display: block;
          }
          
          .header h1 { 
            margin: 3px 0;
            font-size: 18px;
            font-weight: bold;
            text-transform: uppercase;
            color: #000;
          }
          
          .header p {
            font-size: 10px;
            margin: 1px 0;
            color: #000;
          }
          
          .info { 
            margin: 5px 0;
            font-size: 13px;
            line-height: 1.4;
            color: #000;
            font-weight: bold;
          }
          
          .items { 
            margin: 5px 0;
            border-top: 1px dashed #000;
            border-bottom: 1px dashed #000;
            padding: 5px 0;
          }
          
          .items table { 
            width: 100%;
            border-collapse: collapse;
          }
          
          .items th { 
            font-size: 11px;
            font-weight: bold;
            text-align: left;
            padding: 3px 2px;
            border-bottom: 1px solid #000;
            color: #000;
          }
          
          .items td { 
            font-size: 12px;
            font-weight: bold;
            padding: 4px 2px;
            vertical-align: top;
            color: #000;
          }
          
          .item-name {
            font-weight: bold;
            color: #000;
          }
          
          .totals { 
            margin: 5px 0;
            font-size: 12px;
            font-weight: bold;
            color: #000;
          }
          
          .totals div { 
            display: flex;
            justify-content: space-between;
            margin: 2px 0;
            padding: 1px 0;
            color: #000;
          }
          
          .total-final { 
            font-size: 14px;
            font-weight: bold;
            border-top: 2px solid #000;
            padding-top: 4px !important;
            margin-top: 4px !important;
            color: #000;
          }
          
          .footer { 
            margin-top: 6px;
            text-align: center;
            font-size: 11px;
            line-height: 1.3;
            border-top: 1px dashed #000;
            padding-top: 5px;
            color: #000;
            page-break-inside: avoid;
          }
          
          @media print {
            body { 
              margin: 0;
              padding: 6px 8px;
              width: 3in;
              color: #000;
            }
            
            @page {
              size: 3in auto;
              margin: 0;
            }
            
            html, body {
              height: auto;
              overflow: visible;
            }
            
            * {
              color: #000 !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            
            .footer {
              page-break-inside: avoid;
              page-break-before: avoid;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <img src="https://customer-assets.emergentagent.com/job_98f43d7c-462e-47e8-bf72-c54d3fbfbeeb/artifacts/kkp6xmyc_bano_fresh_logo.png" 
               alt="Logo" 
               class="logo">
          <h1>BANO FRESH</h1>
          <p>Premium Meat Shop</p>
        </div>
        
        <div class="info">
          <div><strong>Date:</strong> ${formatDateTime(new Date().toISOString())}</div>
          <div><strong>Customer:</strong> ${customerName}</div>
          ${customerPhone ? `<div style="font-size: 11px; margin-left: 20px; color: #000;">Ph: ${customerPhone}</div>` : ''}
          <div><strong>Payment:</strong> ${paymentMethod.toUpperCase()}</div>
        </div>
        
        <div class="items">
          <table>
            <thead>
              <tr>
                <th style="width: 35%;">Item</th>
                <th style="width: 20%; text-align: center;">Qty</th>
                <th style="width: 22%; text-align: right;">Rate</th>
                <th style="width: 23%; text-align: right;">Amt</th>
              </tr>
            </thead>
            <tbody>
              ${cart.map(item => `
                <tr>
                  <td class="item-name">${item.derived_product_name}</td>
                  <td style="text-align: center;">${item.quantity_kg}kg</td>
                  <td style="text-align: right;">₹${item.selling_price}</td>
                  <td style="text-align: right;">₹${item.total.toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
        
        <div class="totals">
          <div>
            <span>Subtotal:</span>
            <span>₹${totals.subtotal}</span>
          </div>
          ${discount > 0 ? `
          <div>
            <span>Discount (${discount}%):</span>
            <span>-₹${totals.discountAmount}</span>
          </div>
          ` : ''}
          ${taxRate > 0 ? `
          <div>
            <span>Tax (${taxRate}%):</span>
            <span>₹${totals.taxAmount}</span>
          </div>
          ` : ''}
          <div class="total-final">
            <span>TOTAL:</span>
            <span>₹${totals.total}</span>
          </div>
        </div>
        
        <div class="footer">
          <p><strong>Thank you!</strong></p>
          <p>Visit us again</p>
        </div>
        
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              setTimeout(function() {
                window.close();
              }, 100);
            }, 250);
          }
        </script>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank', 'width=288');
    printWindow.document.write(receiptContent);
    printWindow.document.close();
  };

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error("Cart is empty");
      return;
    }

    if (paymentMethod === "credit" && selectedCustomer === "walk-in") {
      toast.error("Credit payment requires a registered customer");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const totals = calculateTotals();

      const saleData = {
        customer_id: selectedCustomer === "walk-in" ? null : selectedCustomer,
        customer_name: selectedCustomer === "walk-in" ? "Walk-in Customer" : customers.find((c) => c.id === selectedCustomer)?.name,
        items: cart,
        subtotal: parseFloat(totals.subtotal),
        tax: parseFloat(totals.taxAmount),
        discount: parseFloat(totals.discountAmount),
        total: parseFloat(totals.total),
        payment_method: paymentMethod,
        sale_date: saleDate,
      };

      await axios.post(`${API_URL}/api/pos-sales`, saleData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Sale completed successfully!");
      
      // Print receipt
      printReceipt(saleData);
      
      // Reset form
      setCart([]);
      setSelectedCustomer("walk-in");
      setDiscount(0);
      setPaymentMethod("cash");
      setSaleDate(new Date().toISOString().split("T")[0]);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to complete sale");
    } finally {
      setLoading(false);
    }
  };

  const totals = calculateTotals();

  return (
    <div className="p-8">
      <div className="flex items-center gap-3 mb-6">
        <ShoppingCart className="h-8 w-8 text-emerald-600" />
        <h1 className="text-3xl font-bold text-gray-900">Point of Sale</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Side - Product Selection */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Add Items</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Main Category *
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Derived Product *
                  </label>
                  <select
                    value={selectedProduct}
                    onChange={(e) => setSelectedProduct(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                    disabled={!selectedCategory}
                  >
                    <option value="">Select Product</option>
                    {derivedProducts.map((prod) => (
                      <option key={prod.id} value={prod.id}>
                        {prod.name} - ₹{prod.selling_price}/kg
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Quantity *
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    value={quantityKg}
                    onChange={(e) => setQuantityKg(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder={
                      selectedProduct && derivedProducts.find(p => p.id === selectedProduct)?.sale_unit === "package"
                        ? "e.g., 3 (packages)"
                        : "e.g., 2.5 (kg)"
                    }
                    autoFocus={false}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {selectedProduct && derivedProducts.find(p => p.id === selectedProduct)?.sale_unit === "package"
                      ? "Enter number of packages"
                      : "Enter weight in kg"}
                  </p>
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={addToCart}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add to Cart
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cart Items */}
          <Card>
            <CardHeader>
              <CardTitle>Cart Items</CardTitle>
            </CardHeader>
            <CardContent>
              {cart.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Cart is empty</p>
              ) : (
                <div className="space-y-2">
                  {cart.map((item, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex-1">
                        <p className="font-semibold">{item.derived_product_name}</p>
                        <p className="text-sm text-gray-600">
                          {item.main_category_name} • {item.quantity_display} × ₹{item.selling_price}{item.sale_unit === "package" ? "/pkg" : "/kg"}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="text-lg font-bold text-emerald-600">
                          ₹{item.total.toFixed(2)}
                        </p>
                        <button
                          onClick={() => handleEditItem(index)}
                          className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                          title="Edit Item"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => removeFromCart(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Side - Checkout */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Customer & Payment</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Customer</label>
                <div className="relative" ref={customerDropdownRef}>
                  <div
                    onClick={() => setShowCustomerDropdown(!showCustomerDropdown)}
                    className="w-full border rounded-lg px-3 py-2 cursor-pointer bg-white flex items-center justify-between hover:border-gray-400"
                  >
                    <span>{getSelectedCustomerName()}</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>

                  {showCustomerDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-80 overflow-hidden">
                      {/* Search Input */}
                      <div className="p-2 border-b sticky top-0 bg-white">
                        <div className="relative">
                          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Search customers..."
                            value={customerSearchTerm}
                            onChange={(e) => setCustomerSearchTerm(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full pl-9 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            autoFocus
                          />
                        </div>
                      </div>

                      {/* Customer List */}
                      <div className="max-h-60 overflow-y-auto">
                        {/* Walk-in Customer Option */}
                        <div
                          onClick={() => handleCustomerSelect("walk-in")}
                          className={`px-4 py-2 cursor-pointer hover:bg-emerald-50 ${
                            selectedCustomer === "walk-in" ? "bg-emerald-100 font-semibold" : ""
                          }`}
                        >
                          Walk-in Customer
                        </div>

                        {/* Filtered Customers */}
                        {filteredCustomers.length > 0 ? (
                          filteredCustomers.map((customer) => (
                            <div
                              key={customer.id}
                              onClick={() => handleCustomerSelect(customer.id)}
                              className={`px-4 py-2 cursor-pointer hover:bg-emerald-50 ${
                                selectedCustomer === customer.id ? "bg-emerald-100 font-semibold" : ""
                              }`}
                            >
                              <div className="flex justify-between items-center">
                                <span>{customer.name}</span>
                                {customer.phone && (
                                  <span className="text-sm text-gray-500">{customer.phone}</span>
                                )}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="px-4 py-3 text-gray-500 text-center">
                            No customers found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Discount (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={discount}
                  onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Tax Rate (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={taxRate}
                  onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Payment Method
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="upi">UPI</option>
                  <option value="credit">Credit</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Sale Date
                </label>
                <input
                  type="date"
                  value={saleDate}
                  onChange={(e) => setSaleDate(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                  max={new Date().toISOString().split("T")[0]}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Order Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-semibold">₹{totals.subtotal}</span>
              </div>
              <div className="flex justify-between text-red-600">
                <span>Discount ({discount}%)</span>
                <span>-₹{totals.discountAmount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Tax ({taxRate}%)</span>
                <span className="font-semibold">₹{totals.taxAmount}</span>
              </div>
              <div className="border-t pt-3 flex justify-between text-lg">
                <span className="font-bold">Total</span>
                <span className="font-bold text-emerald-600">
                  ₹{totals.total}
                </span>
              </div>

              <Button
                onClick={handleCheckout}
                disabled={cart.length === 0 || loading}
                className="w-full bg-emerald-600 hover:bg-emerald-700 mt-4"
              >
                {loading ? "Processing..." : "Complete Sale"}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit Item Dialog */}
      {editingCartIndex !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Edit Item</h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  Product: <span className="font-semibold">{cart[editingCartIndex].derived_product_name}</span>
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  Category: <span className="font-semibold">{cart[editingCartIndex].main_category_name}</span>
                </p>
              </div>

              {/* Quantity Input */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Quantity ({cart[editingCartIndex].sale_unit === "package" ? "packages" : "kg"}) *
                </label>
                <input
                  type="number"
                  step={cart[editingCartIndex].sale_unit === "package" ? "1" : "0.01"}
                  min="0.01"
                  value={editQuantity}
                  onChange={(e) => setEditQuantity(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder={cart[editingCartIndex].sale_unit === "package" ? "Enter packages" : "Enter kg"}
                  autoFocus
                />
              </div>

              {/* Price Input */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Price (₹/{cart[editingCartIndex].sale_unit === "package" ? "pkg" : "kg"}) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={editPrice}
                  onChange={(e) => setEditPrice(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="Enter price"
                />
              </div>

              {/* Summary */}
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-sm text-gray-600">
                  Original: {cart[editingCartIndex].quantity_display} × ₹{cart[editingCartIndex].selling_price} = ₹{cart[editingCartIndex].total.toFixed(2)}
                </p>
                {editQuantity && editPrice && parseFloat(editQuantity) > 0 && parseFloat(editPrice) > 0 && (
                  <p className="text-sm font-semibold text-emerald-600 mt-1">
                    New: {parseFloat(editQuantity)} {cart[editingCartIndex].sale_unit === "package" ? "pkg" : "kg"} × ₹{parseFloat(editPrice)} = ₹{(parseFloat(editQuantity) * parseFloat(editPrice)).toFixed(2)}
                  </p>
                )}
              </div>

              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => {
                    setEditingCartIndex(null);
                    setEditPrice("");
                    setEditQuantity("");
                  }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveItemEdit}
                  className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewPOS;
